import React from "react";
import { Mail, Globe, MapPin, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ComplianceBand } from "@/components/Section";

const CONTACT = {
  company: "Taonga OÜ",
  country: "Estonia",
  email: "info@taonga.eu",
  website: "www.taonga.eu",
  address: "A. Weizenbergi tn 16, 10150, Tallinn, Estonia",
};

const Contact = () => {
  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="bg-white">
        <div className="container-tight pt-16 md:pt-24 pb-12">
          <div className="max-w-3xl">
            <div className="overline reveal">Get in touch</div>
            <h1
              data-testid="contact-title"
              className="reveal reveal-delay-1 mt-6 font-display text-4xl sm:text-5xl lg:text-[60px] font-bold tracking-tight text-navy leading-[1.05]"
            >
              Contact
            </h1>
            <p
              data-testid="contact-intro"
              className="reveal reveal-delay-2 mt-8 text-[18px] leading-relaxed text-charcoal"
            >
              For business enquiries, property-related opportunities or
              partnership discussions, please contact Taonga OÜ.
            </p>
          </div>
        </div>
      </section>

      {/* FORM + DETAILS */}
      <section className="section bg-soft pt-12 md:pt-16">
        <div className="container-tight">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            {/* Form */}
            <div className="lg:col-span-7">
              <div className="bg-white border border-border rounded-md p-8 lg:p-10">
                <h2 className="font-display text-2xl font-semibold text-navy tracking-tight">
                  Send a message
                </h2>
                <p className="mt-2 text-[14px] text-charcoal/80">
                  Submitting opens your email client with the message pre-filled.
                </p>

                <form
                  data-testid="contact-form"
                  action={`mailto:${CONTACT.email}`}
                  method="POST"
                  encType="text/plain"
                  className="mt-8 space-y-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="name" className="text-[13px] text-charcoal">
                        Name
                      </Label>
                      <Input
                        id="name"
                        name="Name"
                        required
                        data-testid="contact-input-name"
                        className="mt-2 h-11 rounded-md border-border focus-visible:ring-navy"
                      />
                    </div>
                    <div>
                      <Label htmlFor="company" className="text-[13px] text-charcoal">
                        Company
                      </Label>
                      <Input
                        id="company"
                        name="Company"
                        data-testid="contact-input-company"
                        className="mt-2 h-11 rounded-md border-border focus-visible:ring-navy"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-[13px] text-charcoal">
                      Email
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      name="Email"
                      required
                      data-testid="contact-input-email"
                      className="mt-2 h-11 rounded-md border-border focus-visible:ring-navy"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-[13px] text-charcoal">
                      Message
                    </Label>
                    <Textarea
                      id="message"
                      name="Message"
                      required
                      rows={6}
                      data-testid="contact-input-message"
                      className="mt-2 rounded-md border-border focus-visible:ring-navy resize-none"
                    />
                  </div>

                  <div className="pt-2">
                    <Button
                      type="submit"
                      data-testid="contact-submit-btn"
                      className="bg-navy hover:bg-navy/90 text-white h-11 px-6 rounded-md text-[15px]"
                    >
                      Send message
                    </Button>
                  </div>

                  <p className="text-[12px] text-charcoal/60 pt-2">
                    By contacting us, you acknowledge that Taonga OÜ does not
                    provide regulated financial services or third-party asset
                    management.
                  </p>
                </form>
              </div>
            </div>

            {/* Details */}
            <aside className="lg:col-span-5" data-testid="contact-details">
              <div className="border border-border rounded-md bg-white p-8 lg:p-10">
                <div className="overline">Contact details</div>
                <h3 className="mt-3 font-display text-xl font-semibold text-navy tracking-tight">
                  {CONTACT.company}
                </h3>
                <ul className="mt-6 space-y-5 text-[15px] text-charcoal">
                  <li className="flex items-start gap-3" data-testid="contact-company">
                    <Building2 className="h-4 w-4 text-warm mt-1 shrink-0" strokeWidth={1.5} />
                    <div>
                      <div className="text-[12px] uppercase tracking-[0.2em] text-charcoal/60">
                        Company
                      </div>
                      <div className="mt-1">{CONTACT.company}</div>
                      <div className="mt-1 text-charcoal/80 text-[14px]">
                        Established 2016 · Registered in Estonia
                      </div>
                      <div className="mt-1 text-charcoal/80 text-[14px]">
                        Registry code:{" "}
                        <span className="font-medium text-navy">14152771</span>
                      </div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3" data-testid="contact-country">
                    <MapPin className="h-4 w-4 text-warm mt-1 shrink-0" strokeWidth={1.5} />
                    <div>
                      <div className="text-[12px] uppercase tracking-[0.2em] text-charcoal/60">
                        Country
                      </div>
                      <div className="mt-1">{CONTACT.country}</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3" data-testid="contact-email">
                    <Mail className="h-4 w-4 text-warm mt-1 shrink-0" strokeWidth={1.5} />
                    <div>
                      <div className="text-[12px] uppercase tracking-[0.2em] text-charcoal/60">
                        Email
                      </div>
                      <a
                        href={`mailto:${CONTACT.email}`}
                        className="mt-1 inline-block text-navy hover:text-warm transition-colors"
                      >
                        {CONTACT.email}
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-3" data-testid="contact-website">
                    <Globe className="h-4 w-4 text-warm mt-1 shrink-0" strokeWidth={1.5} />
                    <div>
                      <div className="text-[12px] uppercase tracking-[0.2em] text-charcoal/60">
                        Website
                      </div>
                      <div className="mt-1">{CONTACT.website}</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3" data-testid="contact-address">
                    <MapPin className="h-4 w-4 text-warm mt-1 shrink-0" strokeWidth={1.5} />
                    <div>
                      <div className="text-[12px] uppercase tracking-[0.2em] text-charcoal/60">
                        Address
                      </div>
                      <div className="mt-1">{CONTACT.address}</div>
                    </div>
                  </li>
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <ComplianceBand testId="contact-compliance-band">
        Taonga OÜ operates exclusively with its own corporate capital and does
        not provide regulated financial services or third-party asset management.
      </ComplianceBand>
    </div>
  );
};

export default Contact;
